// src/pages/forge/ProblemBank.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { getCurrentUserRole, analystAccess, squadAccess } from '@/lib/problemBankAccess';
import { ProblemBankEntry } from '@/types/problemBank';
import {
  Search,
  Filter,
  Grid,
  List,
  ArrowLeft,
  Eye,
  Download,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Building2,
  DollarSign,
  MapPin,
  MoreVertical,
  RefreshCw,
  Sparkles,
  Rocket,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  lead: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Lead' },
  prospect: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Prospect' },
  verified: { bg: 'bg-green-100', text: 'text-green-700', label: 'Verified' },
  assigned: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Assigned' },
  solved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Solved' }
};

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

// Helper function to safely parse JSON fields
function parseProblemData(problem: any): ProblemBankEntry {
  return {
    ...problem,
    id: problem.id,
    problemId: problem.problem_id,
    title: problem.title,
    status: problem.status,
    dateDiscovered: problem.date_discovered,
    source: problem.source,
    category: problem.category || [],
    industrySector: problem.industry_sector,
    anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
    quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
    rootCauseAnalysis: typeof problem.root_cause_analysis === 'string' ? JSON.parse(problem.root_cause_analysis) : problem.rootCauseAnalysis,
    solutionPotential: typeof problem.solution_potential === 'string' ? JSON.parse(problem.solution_potential) : problem.solutionPotential,
    verification: typeof problem.verification === 'string' ? JSON.parse(problem.verification) : problem.verification,
    priorityScore: problem.priority_score,
    assignment: problem.assignment,
    outcome: problem.outcome,
    createdAt: problem.created_at,
    updatedAt: problem.updated_at
  };
}

// Helper function for formatting currency
function formatCurrency(value: number): string {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
}

function ProblemCard({ 
  problem, 
  userRole, 
  onExpressInterest 
}: { 
  problem: ProblemBankEntry; 
  userRole: string | null;
  onExpressInterest: (problem: ProblemBankEntry) => void;
}) {
  const navigate = useNavigate();
  const status = STATUS_COLORS[problem.status] || STATUS_COLORS.lead;
  const priority = problem.priorityScore?.priorityTier || 'medium';

  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer" 
      onClick={() => navigate(`/forge/problem/${problem.id}`)}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base font-semibold line-clamp-1">
              {problem.title}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {problem.problemId} • {problem.anchor?.name || 'Unknown'}
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/forge/problem/${problem.id}`); }}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                <Download className="h-4 w-4 mr-2" />
                Download Dossier
              </DropdownMenuItem>
              {problem.status === 'verified' && userRole === 'squad_member' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onExpressInterest(problem); }}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Express Interest
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`${status.bg} ${status.text}`}>
              {status.label}
            </Badge>
            {problem.priorityScore && (
              <Badge variant="outline" className={PRIORITY_COLORS[priority]}>
                {priority.toUpperCase()}
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Annual Loss</p>
              <p className="font-semibold text-red-600">
                {formatCurrency(problem.quantification?.totalLossPerYear || 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Payback</p>
              <p className="font-semibold">
                {problem.solutionPotential?.anchorPaybackMonths || 0} months
              </p>
            </div>
          </div>

          {problem.assignment && (
            <div className="mt-2 p-2 bg-purple-50 rounded-md">
              <p className="text-xs font-medium text-purple-700">Assigned to squad</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="h-3 w-3" />
          <span className="truncate">{problem.anchor?.name || 'Unknown'}</span>
          <span>•</span>
          <MapPin className="h-3 w-3" />
          <span className="truncate">{problem.anchor?.location || 'Unknown'}</span>
        </div>
      </CardFooter>
    </Card>
  );
}

function ProblemSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-1/2" />
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-3">
          <Skeleton className="h-6 w-24" />
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Skeleton className="h-4 w-full" />
      </CardFooter>
    </Card>
  );
}

export default function ForgeProblemBank() {
  const navigate = useNavigate();
  const { isAuthenticated, squadUser } = useSquadAuth();
  const { toast } = useToast();
  
  const [userRole, setUserRole] = useState<string | null>(null);
  const [problems, setProblems] = useState<ProblemBankEntry[]>([]);
  const [filteredProblems, setFilteredProblems] = useState<ProblemBankEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    minLoss: ''
  });

  // Express interest dialog state
  const [showInterestDialog, setShowInterestDialog] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState<ProblemBankEntry | null>(null);
  const [interestReason, setInterestReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load problems when component mounts, when authentication changes, or when squadUser changes
  useEffect(() => {
    loadUserAndData();
  }, [isAuthenticated, squadUser]);

  // Also reload when the page becomes visible (user returns from another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, reloading problems...');
        loadUserAndData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  async function loadUserAndData() {
    setIsLoading(true);
    try {
      // Get user role - this now checks localStorage for squad session
      const role = await getCurrentUserRole();
      setUserRole(role);
      console.log('🔍 User role:', role);
      console.log('🔍 SquadUser from context:', squadUser);
      console.log('🔍 isAuthenticated:', isAuthenticated);

      let rawData: any[] = [];
      
      if (role === 'analyst' || role === 'admin') {
        rawData = await analystAccess.getProblems({});
      } else if (role === 'squad_member') {
        rawData = await squadAccess.getAvailableProblems();
        console.log('🔍 Raw problems from squadAccess:', rawData?.length || 0, 'problems found');
      } else {
        // If role is null but we have squadUser, try to load as squad member anyway
        if (squadUser) {
          console.log('⚠️ Role was null but squadUser exists, attempting to load problems as squad member');
          rawData = await squadAccess.getAvailableProblems();
        } else {
          console.log('⚠️ No squad authentication found, cannot load problems');
          rawData = [];
        }
      }
      
      // Parse JSON fields for each problem
      const parsedProblems = rawData.map(parseProblemData);
      console.log('🔍 Parsed problems count:', parsedProblems.length);
      
      setProblems(parsedProblems);
      setFilteredProblems(parsedProblems);
      
    } catch (error) {
      console.error('Error loading problems:', error);
      toast({
        title: 'Error',
        description: 'Failed to load problems. Please refresh the page.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleExpressInterest = (problem: ProblemBankEntry) => {
    setSelectedProblem(problem);
    setInterestReason('');
    setShowInterestDialog(true);
  };

  const submitInterest = async () => {
    if (!selectedProblem || !squadUser) {
      toast({
        title: 'Error',
        description: 'Unable to express interest. Please try again.',
        variant: 'destructive'
      });
      return;
    }

    if (!interestReason.trim()) {
      toast({
        title: 'Reason Required',
        description: 'Please explain why your squad is interested in this problem.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await squadAccess.expressInterest(selectedProblem.id, interestReason.trim());
      
      toast({
        title: 'Interest Expressed!',
        description: `Your squad has expressed interest in "${selectedProblem.title}". The analyst will review your request.`,
      });
      
      setShowInterestDialog(false);
      setInterestReason('');
      setSelectedProblem(null);
      
    } catch (error) {
      console.error('Error expressing interest:', error);
      toast({
        title: 'Error',
        description: 'Failed to express interest. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter problems based on search and filters
  useEffect(() => {
    let filtered = [...problems];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.problemId?.toLowerCase().includes(query) ||
        p.anchor?.name?.toLowerCase().includes(query)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.priority) {
      filtered = filtered.filter(p => p.priorityScore?.priorityTier === filters.priority);
    }

    if (filters.minLoss) {
      const minLossNum = parseFloat(filters.minLoss) * 1000000;
      filtered = filtered.filter(p => (p.quantification?.totalLossPerYear || 0) >= minLossNum);
    }

    setFilteredProblems(filtered);
  }, [searchQuery, filters, problems]);

  const stats = {
    total: filteredProblems.length,
    verified: filteredProblems.filter(p => p.status === 'verified').length,
    assigned: filteredProblems.filter(p => p.status === 'assigned').length,
    totalLoss: filteredProblems.reduce((sum, p) => sum + (p.quantification?.totalLossPerYear || 0), 0) / 1000000
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/forge/dashboard')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Problem Bank</h1>
          <p className="text-muted-foreground text-sm">
            {userRole === 'analyst' && 'Manage and verify industrial problems'}
            {userRole === 'squad_member' && 'Browse verified problems and express interest'}
            {userRole === 'admin' && 'Administrator access to all problems'}
            {!userRole && 'Loading...'}
          </p>
        </div>
        
        <Button variant="outline" onClick={() => loadUserAndData()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        
        <Button variant="outline" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
          {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
        </Button>
      </div>

      {/* Stats Cards - Only show if there are problems or not loading */}
      {!isLoading && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Problems</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {userRole === 'squad_member' ? 'Available' : 'Verified'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.verified}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Loss</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">KES {stats.totalLoss.toFixed(1)}M</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search problems, companies, or IDs..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Statuses</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="solved">Solved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Priority</label>
                  <Select
                    value={filters.priority}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Priorities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Min Loss (KES M/year)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 10"
                    value={filters.minLoss}
                    onChange={(e) => setFilters(prev => ({ ...prev, minLoss: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFilters({ status: '', priority: '', minLoss: '' })}
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <ProblemSkeleton key={i} />)}
        </div>
      ) : filteredProblems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No problems found</h3>
            <p className="text-muted-foreground text-center max-w-md">
              {userRole === 'analyst' 
                ? 'Start by adding a new problem or reviewing external submissions.'
                : 'There are currently no verified problems available. Check back later!'}
            </p>
            <Button 
              variant="outline" 
              className="mt-4" 
              onClick={() => loadUserAndData()}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProblems.map(problem => (
            <ProblemCard 
              key={problem.id} 
              problem={problem} 
              userRole={userRole} 
              onExpressInterest={handleExpressInterest}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProblems.map(problem => (
            <Card 
              key={problem.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/forge/problem/${problem.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">{problem.problemId}</span>
                      <Badge variant="outline" className={STATUS_COLORS[problem.status]?.bg + ' ' + STATUS_COLORS[problem.status]?.text}>
                        {STATUS_COLORS[problem.status]?.label}
                      </Badge>
                    </div>
                    <h3 className="font-medium mb-1">{problem.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {problem.anchor?.name || 'Unknown'} • {problem.anchor?.location || 'Unknown'}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-semibold text-red-600">
                      {formatCurrency(problem.quantification?.totalLossPerYear || 0)}/yr
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Express Interest Dialog - PROPER SHADCN DIALOG */}
      <Dialog open={showInterestDialog} onOpenChange={setShowInterestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-green-600" />
              Express Interest in Problem
            </DialogTitle>
            <DialogDescription>
              Tell us why your squad is interested in solving this problem.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/30 p-3 rounded-lg">
              <p className="text-sm font-medium">{selectedProblem?.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedProblem?.anchor?.name} • {formatCurrency(selectedProblem?.quantification?.totalLossPerYear || 0)} annual loss
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="interestReason">Why is your squad interested in this problem?</Label>
              <Textarea
                id="interestReason"
                placeholder="e.g., This problem aligns with our squad's expertise in manufacturing optimization, and we believe we can deliver a cost-effective solution within 60 days..."
                value={interestReason}
                onChange={(e) => setInterestReason(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                This helps the analyst understand your squad's capability and commitment.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowInterestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitInterest} 
              disabled={isSubmitting || !interestReason.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Submit Interest
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}